import React, { useEffect, useState, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useSelector } from 'react-redux';
import { getClients } from '../../../store/slices/clientsSlice';
import { getJobs } from '../../../store/slices/jobsSlice';
import { getTechnicians } from '../../../store/slices/usersSlice';
import { getUser } from '../../../store/slices/userSlice';
import TechnicianDetailsDialog from '../technicians/TechnicianDetailsDialog';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import ClientDetailsDialog from '../clients/ClientDetailsDialog';
import { isTechnicianArchived } from '../../../utils/technicianStatus';
import { Button, useTheme } from '@mui/material';
import { isActiveStatus, isCompletedStatus, isCancelledStatus } from '../../../utils/jobStatuses';
import { isOnlyDispatcher } from '../../../utils/permissions';
import useIsMobile from '../../../hooks/useIsMobile';

const containerStyle = {
    width: '100%',
    height: '100vh'
};

const defaultCenter = {
    lat: 44.8283,
    lng: -98.5795
};

const defaultOptions = {
    zoom: 1,
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: true,
    mapTypeControl: true,
    fullscreenControl: true,
};

// Helper function to get display name for technician/vendor
const getTechnicianDisplayName = (tech) => {
    if (!tech) return '';
    if (tech.type === 'vendor') {
        return tech.company_name || '';
    }
    return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

// Marker colors for different types
const markerColors = {
    client: '#1976d2', // Blue
    technician: '#2e7d32', // Green
    technicianArchived: '#9e9e9e', // Gray
    job: '#ed6c02', // Orange
};

const Map = () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const mapRef = useRef(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    
    // Filter states
    const [showClients, setShowClients] = useState(true);
    const [showTechnicians, setShowTechnicians] = useState(true);
    const [showJobs, setShowJobs] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMinimized, setIsMinimized] = useState(false);
    
    // Drag states
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x: null, y: null });
    const paperRef = useRef(null);

    // Theme
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const isMobile = useIsMobile();

    // Redux data
    const clientsData = useSelector(getClients);
    const jobsData = useSelector(getJobs);
    const techniciansData = useSelector(getTechnicians);
    const user = useSelector(getUser);
    const dispatcherOnly = isOnlyDispatcher(user);
    const canShowClientMarkers = !dispatcherOnly && showClients;

    // Check if Google Maps API is already loaded (from LoadScript in MainDrawer)
    useEffect(() => {
        const checkGoogleMaps = () => {
            if (window.google && window.google.maps) {
                setIsScriptLoaded(true);
            } else {
                // Check periodically if script is loading
                const interval = setInterval(() => {
                    if (window.google && window.google.maps) {
                        setIsScriptLoaded(true);
                        clearInterval(interval);
                    }
                }, 100);
                
                // Stop checking after 10 seconds
                setTimeout(() => {
                    clearInterval(interval);
                }, 10000);
            }
        };

        checkGoogleMaps();
    }, []);

    // Handle map instance when it loads
    const onMapLoad = (map) => {
        mapRef.current = map;
    };

    // Drag handlers
    const handleMouseDown = (e) => {
        if (isMinimized || isMobile) return;
        // Don't start dragging if clicking on the button or input fields
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('[role="checkbox"]')) return;
        
        if (paperRef.current) {
            const rect = paperRef.current.getBoundingClientRect();
            // Calculate the actual center position of the element (accounting for transform)
            const currentCenterX = rect.left + rect.width / 2;
            const currentCenterY = rect.top;
            
            // Initialize position if not set - use the current visual center position
            // Use flushSync to ensure position is set synchronously before calculating offset
            let centerX, centerY;
            if (position.x === null || position.y === null) {
                centerX = currentCenterX;
                centerY = currentCenterY;
                // Flush the position update synchronously to avoid shift
                flushSync(() => {
                    setPosition({
                        x: centerX,
                        y: centerY,
                    });
                });
            } else {
                centerX = position.x;
                centerY = position.y;
            }
            
            // Calculate offset from click position to the center of the element
            // Now the position is guaranteed to be set, so offset calculation is accurate
            setDragOffset({
                x: e.clientX - centerX,
                y: e.clientY - centerY,
            });
            
            // Set dragging state
            setIsDragging(true);
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || isMinimized) return;
            // Use the drag offset to calculate new position
            // The drag offset was calculated relative to the center position
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y,
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            // Use capture phase and immediate execution to ensure smooth dragging
            document.addEventListener('mousemove', handleMouseMove, { passive: true });
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, isMinimized]);

    // Reset position when minimized
    useEffect(() => {
        if (isMinimized) {
            setPosition({ x: null, y: null });
        }
    }, [isMinimized]);

    // Filter and prepare data for markers
    const filteredMarkers = useMemo(() => {
        const markers = [];

        // Filter clients (hidden for dispatcher-only users)
        if (canShowClientMarkers && clientsData.clients) {
            clientsData.clients
                .filter(client => 
                    client.latitude != null && 
                    client.longitude != null &&
                    (!searchQuery || 
                        client.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.state?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.client_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        client.display_as?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                )
                .forEach(client => {
                    markers.push({
                        id: `client-${client.id}`,
                        type: 'client',
                        position: { lat: parseFloat(client.latitude), lng: parseFloat(client.longitude) },
                        data: client,
                        title: client.client_name,
                        info: {
                            name: client.client_name,
                            address: client.client_address,
                            city: client.city,
                            state: client.state,
                            zip: client.zip_code,
                            phone: client.phone,
                            email: client.email,
                            status: client.client_status ? 'Active' : 'Inactive',
                        }
                    });
                });
        }

        // Filter technicians
        if (showTechnicians && techniciansData) {
            techniciansData
                .filter(technician => 
                    technician.latitude != null && 
                    technician.longitude != null &&
                    (!searchQuery || 
                        technician.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        technician.state?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                )
                .forEach(technician => {
                    const isArchived = isTechnicianArchived(technician);
                    markers.push({
                        id: `technician-${technician.id}`,
                        type: 'technician',
                        markerColor: isArchived ? markerColors.technicianArchived : markerColors.technician,
                        isArchived,
                        position: { lat: parseFloat(technician.latitude), lng: parseFloat(technician.longitude) },
                        data: technician,
                        title: getTechnicianDisplayName(technician),
                        info: {
                            name: getTechnicianDisplayName(technician),
                            address: technician.address,
                            city: technician.city,
                            state: technician.state,
                            zip: technician.zip,
                            phone: technician.phone,
                            email: technician.email,
                            travelDistance: technician.travel_distance ? `${technician.travel_distance} mi` : 'N/A',
                            isArchived,
                        }
                    });
                });
        }

        // Filter jobs
        if (showJobs && jobsData.jobs) {
            jobsData.jobs
                .filter(job => 
                    job.latitude != null && 
                    job.longitude != null &&
                    (!searchQuery || 
                        job.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        job.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        job.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        job.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        job.state?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        job.trade?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                )
                .forEach(job => {
                    markers.push({
                        id: `job-${job.id}`,
                        type: 'job',
                        position: { lat: parseFloat(job.latitude), lng: parseFloat(job.longitude) },
                        data: job,
                        title: job.description || 'Job',
                        info: {
                            description: job.description || 'No description',
                            client: job.client_name,
                            address: job.address,
                            city: job.city,
                            state: job.state,
                            zip: job.zip_code,
                            trade: job.trade || 'N/A',
                            status: (() => {
                                const s = Number(job.status);
                                if (isCancelledStatus(s)) return 'Cancelled';
                                if (isCompletedStatus(s)) return 'Completed';
                                if (isActiveStatus(s)) return 'In Progress';
                                return 'Unknown';
                            })(),
                            priority: job.priority === '1' ? 'High' : job.priority === '2' ? 'Medium' : 'Low',
                            is_locked: !!job.is_locked,
                        }
                    });
                });
        }

        return markers;
    }, [clientsData, jobsData, techniciansData, canShowClientMarkers, showTechnicians, showJobs, searchQuery]);

    // Adjust map bounds to fit all visible markers
    useEffect(() => {
        if (mapRef.current && filteredMarkers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            filteredMarkers.forEach(marker => {
                bounds.extend(marker.position);
            });
            mapRef.current.fitBounds(bounds);
        }
    }, [filteredMarkers]);

    // Create custom marker icon
    const createMarkerIcon = (color) => {
        if (!window.google || !window.google.maps) {
            return undefined; // Use default marker if Google Maps not loaded
        }
        return {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
            scale: 18,
        };
    };

    if (!apiKey) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                }}
            >
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                        Google Maps API Key Required
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                        Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (!isScriptLoaded) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                }}
            >
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Loading Google Maps...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100vh', position: 'relative' }}>
            {/* Filter Bar */}
            <Paper
                ref={paperRef}
                elevation={3}
                onMouseDown={handleMouseDown}
                sx={{
                    position: 'absolute',
                    top: isMinimized ? 0 : (position.y !== null ? `${position.y}px` : 16),
                    left: isMinimized ? '50%' : (isMobile ? 16 : (position.x !== null ? `${position.x}px` : '50%')),
                    transform: (!isMinimized && isMobile) ? 'none' : 'translateX(-50%)',
                    zIndex: 1000,
                    p: isMinimized ? '4px 12px' : { xs: 1.5, sm: 2 },
                    bgcolor: 'background.paper',
                    borderRadius: isMinimized ? '0 0 8px 8px' : 2,
                    maxWidth: isMinimized ? 'none' : '600px',
                    width: isMinimized ? 'auto' : 'calc(100% - 32px)',
                    minWidth: isMinimized ? 'auto' : 'auto',
                    transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    cursor: isMinimized ? 'default' : (isMobile ? 'default' : (isDragging ? 'grabbing' : 'grab')),
                    userSelect: 'none',
                    boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.12)',
                    border: isDarkMode ? '1px solid #334155' : '1px solid #f0f0f0',
                }}
            >
                {/* Minimize/Expand Button */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mb: isMinimized ? 0 : 1,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={() => setIsMinimized(!isMinimized)}
                        sx={{
                            color: 'text.secondary',
                            padding: isMinimized ? '4px' : '8px',
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            },
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: isMinimized ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        {isMinimized ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                </Box>

                {/* Collapsible Content with Animation */}
                <Collapse in={!isMinimized} timeout={300}>
                    <Box>
                        {/* Search Field */}
                        <TextField
                            fullWidth
                            placeholder="Search by name, address, city, state, phone, email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    bgcolor: 'background.paper',
                                    fontSize: '0.875rem',
                                    height: '42px',
                                    '& input': {
                                        color: 'text.primary',
                                    },
                                    '& input::placeholder': {
                                        color: 'text.secondary',
                                        opacity: 1,
                                    },
                                    '& fieldset': {
                                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                                        borderWidth: '1px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                                        borderWidth: '1px',
                                    },
                                }
                            }}
                        />

                        {/* Filter Toggles */}
                        <FormGroup row sx={{ gap: { xs: 1, sm: 2 } }}>
                            {!dispatcherOnly && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showClients}
                                        onChange={(e) => setShowClients(e.target.checked)}
                                        sx={{
                                            color: markerColors.client,
                                            '&.Mui-checked': {
                                                color: markerColors.client,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 10, sm: 12 },
                                                height: { xs: 10, sm: 12 },
                                                borderRadius: '50%',
                                                backgroundColor: markerColors.client,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'text.primary' }}>
                                            Clients
                                        </Typography>
                                    </Box>
                                }
                            />
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showTechnicians}
                                        onChange={(e) => setShowTechnicians(e.target.checked)}
                                        sx={{
                                            color: markerColors.technician,
                                            '&.Mui-checked': {
                                                color: markerColors.technician,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 10, sm: 12 },
                                                height: { xs: 10, sm: 12 },
                                                borderRadius: '50%',
                                                backgroundColor: markerColors.technician,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'text.primary' }}>
                                            Technicians
                                        </Typography>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showJobs}
                                        onChange={(e) => setShowJobs(e.target.checked)}
                                        sx={{
                                            color: markerColors.job,
                                            '&.Mui-checked': {
                                                color: markerColors.job,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 10, sm: 12 },
                                                height: { xs: 10, sm: 12 },
                                                borderRadius: '50%',
                                                backgroundColor: markerColors.job,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'text.primary' }}>
                                            Jobs
                                        </Typography>
                                    </Box>
                                }
                            />
                        </FormGroup>

                        {/* Results count */}
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                            Showing {filteredMarkers.length} location{filteredMarkers.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Collapse>
            </Paper>

            {/* Map */}
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={defaultCenter}
                zoom={defaultOptions.zoom}
                options={defaultOptions}
                onLoad={onMapLoad}
            >
                {/* Render markers */}
                {filteredMarkers.map(marker => (
                    <Marker
                        key={marker.id}
                        position={marker.position}
                        title={marker.title}
                        icon={createMarkerIcon(marker.markerColor || markerColors[marker.type])}
                        onClick={() => setSelectedMarker(marker)}
                    />
                ))}

                {/* InfoWindow for selected marker */}
                {selectedMarker && (
                    <InfoWindow
                        position={selectedMarker.position}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <Box sx={{ p: 1, minWidth: '200px', maxWidth: '300px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: markerColors[selectedMarker.type] }}>
                                {selectedMarker.type === 'client' && 'Client'}
                                {selectedMarker.type === 'technician' && 'Technician'}
                                {selectedMarker.type === 'job' && 'Job'}
                            </Typography>
                            {selectedMarker.type === 'client' && (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {selectedMarker.info.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        {selectedMarker.info.address}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        {selectedMarker.info.city}, {selectedMarker.info.state} {selectedMarker.info.zip}
                                    </Typography>
                                    {selectedMarker.info.phone && (
                                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                            Phone: {selectedMarker.info.phone}
                                        </Typography>
                                    )}
                                    {selectedMarker.info.email && (
                                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                            Email: {selectedMarker.info.email}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                        Status: {selectedMarker.info.status}
                                    </Typography>
                                </>
                            )}

                            {selectedMarker.type === 'technician' && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selectedMarker.info.name}
                                        </Typography>
                                        {selectedMarker.info.isArchived && (
                                            <ArchiveIcon sx={{ fontSize: '1rem', color: '#9e9e9e' }} />
                                        )}
                                    </Box>
                                    {selectedMarker.info.isArchived && (
                                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', mb: 0.5, fontWeight: 600 }}>
                                            Archived
                                        </Typography>
                                    )}
                                    {selectedMarker.info.address && (
                                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                            {selectedMarker.info.address}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        {selectedMarker.info.city}, {selectedMarker.info.state} {selectedMarker.info.zip || ''}
                                    </Typography>
                                    {selectedMarker.info.phone && (
                                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                            Phone: {selectedMarker.info.phone}
                                        </Typography>
                                    )}
                                    {selectedMarker.info.email && (
                                        <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                            Email: {selectedMarker.info.email}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                        Travel Distance: {selectedMarker.info.travelDistance}
                                    </Typography>
                                </>
                            )}

                            {selectedMarker.type === 'job' && (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {selectedMarker.info.is_locked && (
                                            <span title="Job locked - read only" style={{ color: '#ed6c02' }}>🔒</span>
                                        )}
                                        {selectedMarker.info.description}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        Client: {selectedMarker.info.client}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        {selectedMarker.info.address}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        {selectedMarker.info.city}, {selectedMarker.info.state} {selectedMarker.info.zip || ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        Trade: {selectedMarker.info.trade}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                                        Status: {selectedMarker.info.status}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                        Priority: {selectedMarker.info.priority}
                                    </Typography>
                                </>
                            )}
                            <Button variant="contained" color="primary" onClick={() => {
                                if (selectedMarker.type === 'client') {
                                    setSelectedClient(selectedMarker.data);
                                } else if (selectedMarker.type === 'technician') {
                                    setSelectedTechnician(selectedMarker.data);
                                } else if (selectedMarker.type === 'job') {
                                    setSelectedJob(selectedMarker.data);
                                }
                            }}>
                                View Details
                            </Button>
                        </Box>
                    </InfoWindow>
                )}
            </GoogleMap>

            {/* Technician details dialog */}
            <TechnicianDetailsDialog 
                selectedTechnician={selectedTechnician} 
                setSelectedTechnician={setSelectedTechnician} 
                user={user} 
            />

            {/* Job details dialog */}
            <JobDetailsDialog 
                selectedJob={selectedJob} 
                setSelectedJob={setSelectedJob} 
                user={user} 
            />

            {/* Client details dialog (not available for dispatcher-only users) */}
            {!dispatcherOnly && (
            <ClientDetailsDialog 
                selectedClient={selectedClient} 
                setSelectedClient={setSelectedClient} 
                user={user} 
            />
            )}
        </Box>
    );
};

export default Map;

